import axios from 'axios';

/**
 * Error messages for common HTTP status codes.
 */
const HTTP_ERROR_MESSAGES: Record<number, string> = {
  400: 'Invalid request. Please check your input and try again.',
  401: 'Your session has expired. Please log in again.',
  403: 'You don\'t have permission to perform this action.',
  404: 'The requested resource was not found.',
  409: 'This operation conflicts with the current state. Please refresh and try again.',
  422: 'The provided data is invalid. Please check your input.',
  429: 'Too many requests. Please wait a moment and try again.',
  500: 'Server error. Please try again later.',
  502: 'Server is temporarily unavailable. Please try again later.',
  503: 'Service unavailable. Please try again later.',
  504: 'Request timed out. Please try again.',
};

/**
 * Default error message when the specific error cannot be determined.
 */
const DEFAULT_ERROR_MESSAGE = 'An unexpected error occurred. Please try again.';

/**
 * Network error message for connection failures.
 */
const NETWORK_ERROR_MESSAGE = 'Unable to connect to the server. Please check your internet connection.';

/**
 * Extracts a user-friendly error message from an error object.
 * Handles Axios errors, standard Error objects, and unknown error types.
 *
 * @param error The error to extract a message from
 * @param fallbackMessage Optional custom fallback message
 * @returns A user-friendly error message string
 */
export function getErrorMessage(error: unknown, fallbackMessage?: string): string {
  // Handle Axios errors
  if (axios.isAxiosError(error)) {
    // Network error (no response received)
    if (!error.response) {
      if (error.code === 'ECONNABORTED') {
        return 'Request timed out. Please try again.';
      }
      return NETWORK_ERROR_MESSAGE;
    }

    const status = error.response.status;

    // Try to get message from response data first
    const responseMessage = error.response.data?.message || error.response.data?.detail;
    if (responseMessage && typeof responseMessage === 'string') {
      return responseMessage;
    }

    // Fall back to status-based message
    if (HTTP_ERROR_MESSAGES[status]) {
      return HTTP_ERROR_MESSAGES[status];
    }

    // Generic server error for 5xx
    if (status >= 500) {
      return 'Server error. Please try again later.';
    }

    // Generic client error for 4xx
    if (status >= 400) {
      return 'Request failed. Please try again.';
    }
  }

  // Handle standard Error objects
  if (error instanceof Error) {
    // Don't expose technical error messages to users
    // But log them for debugging
    console.error('Error details:', error.message);
    return fallbackMessage || DEFAULT_ERROR_MESSAGE;
  }

  // Handle string errors
  if (typeof error === 'string') {
    return error;
  }

  return fallbackMessage || DEFAULT_ERROR_MESSAGE;
}

/**
 * Logs an error with context for debugging purposes.
 *
 * @param context Description of where the error occurred
 * @param error The error object
 */
export function logError(context: string, error: unknown): void {
  console.error(`[${context}]`, error);

  if (axios.isAxiosError(error)) {
    console.error('Response status:', error.response?.status);
    console.error('Response data:', error.response?.data);
  }
}

/**
 * Checks if an error is a specific HTTP status code.
 *
 * @param error The error to check
 * @param status The HTTP status code to check for
 * @returns true if the error matches the status code
 */
export function isHttpError(error: unknown, status: number): boolean {
  return axios.isAxiosError(error) && error.response?.status === status;
}

/**
 * Checks if an error is an authentication error (401).
 */
export function isAuthError(error: unknown): boolean {
  return isHttpError(error, 401);
}

/**
 * Checks if an error is a permission error (403).
 */
export function isPermissionError(error: unknown): boolean {
  return isHttpError(error, 403);
}

/**
 * Checks if an error is a not found error (404).
 */
export function isNotFoundError(error: unknown): boolean {
  return isHttpError(error, 404);
}

/**
 * Checks if an error is a network/connection error.
 */
export function isNetworkError(error: unknown): boolean {
  return axios.isAxiosError(error) && !error.response;
}
